// src/experiments/exposure.js
//
// Exposure is the moment a visitor actually sees a tested surface, and it is
// the only moment that counts them into an experiment. It fires from the
// component that renders the surface, at most once per experiment per page
// view, and never for crawlers or unenrolled visitors. Firing at assignment
// time instead would count people who never reached the change and dilute a
// real effect into noise.
//
// Two records fall out of an exposure:
//   - the `experiment_viewed` event, with the GA4 parameter names GrowthBook's
//     integration uses (`experiment_id`, `variation_id`), so the data stays
//     readable by its stats engine if we ever move over;
//   - a note in sessionStorage that this visitor has seen this experiment, so
//     every later dataLayer event in the session is stamped with it, including
//     the conversion pages a test does not target.

import * as Sentry from '@sentry/react';
import { getAssignments, isForced } from './assignments';
import { formatAssignments } from './format';
import { findExperiment, getMeta } from './registry';
import {
  getSessionExposures,
  rememberExposure,
  resetSessionExposures,
} from './sessionExposures';

export { EXPOSURE_SESSION_KEY, getSessionExposures } from './sessionExposures';

let exposedThisView = new Set();
let lastPathname = null;

function currentPathname() {
  try {
    return window.location.pathname;
  } catch {
    return '/';
  }
}

// Sentry tags mirror the stamped set, so an error can be filtered by variant.
// Tags for experiments that drop out of the set are cleared again.
let taggedKeys = new Set();
let lastTagged = '';
function syncSentryTags(stamped) {
  try {
    const serialised = formatAssignments(stamped);
    if (serialised === lastTagged) return;
    lastTagged = serialised;
    const next = new Set(Object.keys(stamped));
    for (const key of taggedKeys) {
      if (!next.has(key)) Sentry.setTag(`exp.${key}`, undefined);
    }
    for (const [key, variant] of Object.entries(stamped)) {
      Sentry.setTag(`exp.${key}`, variant);
    }
    taggedKeys = next;
  } catch {
    // Sentry unavailable
  }
}

// What every dataLayer event is stamped with: the assignments in force on
// this page plus everything exposed earlier in the session.
export function getStampedAssignments() {
  let stamped = {};
  try {
    stamped = { ...getSessionExposures(), ...getAssignments() };
  } catch {
    stamped = {};
  }
  syncSentryTags(stamped);
  return stamped;
}

export function getExperimentsString() {
  return formatAssignments(getStampedAssignments());
}

// Records that the surface for `experimentKey` has rendered for this visitor.
// Returns the pushed payload, or null when nothing was pushed.
export function recordExposure(experimentKey) {
  try {
    const pathname = currentPathname();
    if (pathname !== lastPathname) {
      exposedThisView = new Set();
      lastPathname = pathname;
    }

    const assignments = getAssignments();
    if (!Object.prototype.hasOwnProperty.call(assignments, experimentKey)) {
      return null;
    }
    const variant = assignments[experimentKey];
    const onceKey = `${experimentKey}:${variant}`;
    if (exposedThisView.has(onceKey)) return null;
    exposedThisView.add(onceKey);

    rememberExposure(experimentKey, variant);

    const experiment = findExperiment(experimentKey);
    const meta = getMeta(experimentKey);
    const payload = {
      event: 'experiment_viewed',
      experiment_id: experimentKey,
      variation_id: variant,
      experiments: getExperimentsString(),
      // The effective values in force, as flat fields: GA4 truncates parameter
      // values at 100 characters, so a JSON blob would not survive.
      experiment_traffic: Number(experiment?.traffic) || 0,
      experiment_force: meta.force ?? 'none',
      experiment_source: isForced(experimentKey)
        ? 'override'
        : (meta.source ?? 'code'),
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    return payload;
  } catch {
    return null;
  }
}

// Forgets every exposure for this tab. Used by tests.
export function resetExposures() {
  exposedThisView = new Set();
  lastPathname = null;
  taggedKeys = new Set();
  lastTagged = '';
  resetSessionExposures();
}
